'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DudakDolgusununAlerjiRiski() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dudak-dolgusunun-alerji-riski-gercek-olgular-yanlis-algilar-ve-guvenli-uygulama';
  const pageTitle = 'Dudak Dolgusunun Alerji Riski: Gerçek Olgular, Yanlış Algılar ve Güvenli Uygulama';
  const pageDescription = 'Dudak dolgu alerji yapar mı? Hyaluronik asit reaksiyonları, bağışıklık sistemi yanıtı ve güvenli uygulama kriterleri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dudak Dolgusunun Alerji Riski', href: '/tr/blog/dudak-dolgusunun-alerji-riski-gercek-olgular-yanlis-algilar-ve-guvenli-uygulama' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dudak dolgusu alerji riski ve güvenli uygulama"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dudak Estetiği
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dudak Dolgusunun Alerji Riski: Gerçek Olgular, Yanlış Algılar ve Güvenli Uygulama
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>7 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>10 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dudak dolgusu, yüze simetri ve canlılık kazandıran, modern estetik tıbbın en popüler uygulamalarından biridir.
                Ancak sosyal medyada sıkça karşılaşılan "dudak dolgusuna alerjim oldu" söylemleri, hastalarda kafa karışıklığı yaratabiliyor.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Peki dudak dolgusu gerçekten alerji yapar mı?
                Yoksa bu tepkilerin çoğu, normal fizyolojik iyileşme sürecinin bir parçası mı?
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Dudak Dolgusunun İçeriği
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Günümüzde kullanılan dolgu maddelerinin %95'i hyaluronik asit (HA) temellidir.
                Bu madde:
              </p>

              <div className="bg-pink-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-pink-800">
                  <li>İnsan vücudunda doğal olarak bulunur,</li>
                  <li>Ciltte su tutarak nem ve hacim sağlar,</li>
                  <li>Biyolojik olarak parçalanabilir.</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Kısacası: vücut için yabancı değil, tanıdık bir bileşendir.
                Bu nedenle gerçek alerji oranı son derece düşüktür.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Gerçek Alerji ile Normal Tepkiler Arasındaki Fark
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-pink-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Belirti</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Alerji mi?</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kızarıklık</td>
                      <td className="border border-gray-300 px-4 py-3">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Enjeksiyon travması, 6–12 saatte azalır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Ödemdir, 1–3 günde iner</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kaşıntı</td>
                      <td className="border border-gray-300 px-4 py-3">🔸 Belki</td>
                      <td className="border border-gray-300 px-4 py-3">Duyarlılık olabilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ağrı ve sıcaklık hissi</td>
                      <td className="border border-gray-300 px-4 py-3">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Enjeksiyon sonrası normal inflamasyon</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yaygın döküntü, dudakta kabarcık</td>
                      <td className="border border-gray-300 px-4 py-3">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Nadir ama alerjik reaksiyon belirtisi</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Klinik olarak "gerçek alerji" oranı yaklaşık %0,02 civarındadır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Alerjiyi Tetikleyebilecek Faktörler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Faktör</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hayvansal kaynaklı HA içeren eski dolgu ürünleri</td>
                      <td className="border border-gray-300 px-4 py-3">Artık kullanılmıyor, ama geçmişte alerjiye yol açabiliyordu</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Lidokain içeren dolgular</td>
                      <td className="border border-gray-300 px-4 py-3">Bazı hastalarda lokal anestezik duyarlılığı görülebilir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kronik bağışıklık hastalıkları</td>
                      <td className="border border-gray-300 px-4 py-3">Aşırı immün yanıt riski</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yanlış enjeksiyon derinliği</td>
                      <td className="border border-gray-300 px-4 py-3">Lokal inflamasyonla karıştırılabilir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Bilimsel Veriler
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                ISAPS (2024) raporuna göre:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Alerjik reaksiyon oranı: %0,02</li>
                  <li>Gerçek immün sistem temelli vakalar: %0,003</li>
                  <li>En sık neden: lidokain duyarlılığı</li>
                </ul>
                <p className="text-blue-800 mt-4">
                  FDA onaylı markalarda bu oran %0,001'in altına düşmektedir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Dudak Dolgusu Sonrası Alerji Belirtileri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Belirti</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Başlama Zamanı</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif kaşıntı</td>
                      <td className="border border-gray-300 px-4 py-3">İlk 6 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Genellikle geçicidir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şişlik ve sertlik hissi</td>
                      <td className="border border-gray-300 px-4 py-3">1–3 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Ödem kaynaklı</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Döküntü / kızarıklık</td>
                      <td className="border border-gray-300 px-4 py-3">6–12 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Hafif alerjik duyarlılık olabilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dudakta nodül (sertlik)</td>
                      <td className="border border-gray-300 px-4 py-3">2–7 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Enfeksiyon veya aşırı doku tepkisi olabilir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Solunum güçlüğü / yüz genelinde şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">Dakikalar</td>
                      <td className="border border-gray-300 px-4 py-3">Ciddi anafilaktik reaksiyon (çok nadir)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Klinik Vaka Örneği
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastam, dudak dolgusu sonrası 2. gün dudakta kaşıntı ve hafif yanma hissetti.
                  Muayenede ciddi alerji değil, lidokain duyarlılığı olduğu anlaşıldı.
                  Antihistaminik tedaviyle 24 saatte tamamen geçti."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Alerji Testi Gerekir mi?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Evet, özellikle geçmişte ilaç veya anestezik alerjisi olan hastalarda ön test yapılabilir.
                Kolun iç yüzüne çok küçük bir dolgu miktarı enjekte edilerek 15–30 dakika izlenir.
                Kabarıklık veya kaşıntı oluşmazsa işlem güvenle yapılabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Alerji Gelişirse Ne Yapılır?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-yellow-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Müdahale</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif kızarıklık / kaşıntı</td>
                      <td className="border border-gray-300 px-4 py-3">Antihistaminik, soğuk kompres</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Bölgesel ödem</td>
                      <td className="border border-gray-300 px-4 py-3">Kortizon kremi, buz uygulaması</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nodül veya sertlik</td>
                      <td className="border border-gray-300 px-4 py-3">Hyaluronidaz enzimi ile dolgunun çözülmesi</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sistemik reaksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Acil servis, adrenalin (epinefrin) tedavisi</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 Gerçek alerji yönetilebilir; önemli olan erken fark etmektir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Hyaluronidaz Enzimi Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hyaluronidaz, hyaluronik asidi parçalayan doğal bir enzimdir.
                Eğer vücut dolguya aşırı tepki verirse, bu enzimle dolgu tamamen ve güvenle eritilebilir.
                Bu işlem hem alerjide hem de istenmeyen estetik sonuçlarda kullanılabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Estetik ve Etik Boyut
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Alerji riski düşük olsa da, hasta bilgilendirilmeden yapılan her işlem etik değildir.
                Dudak dolgusu öncesinde kullanılan marka, içerik ve olası riskler mutlaka paylaşılmalıdır.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 italic">
                  "Güzellik bilgilendirmeyle başlar; bilgi yoksa risk vardır."
                </p>
                <p className="text-purple-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dudak dolgusuna alerjim olabilir mi?</h3>
                  <p className="text-gray-600">Çok nadirdir, genellikle lidokain duyarlılığı şeklinde görülür.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Alerji olursa ne olur?</h3>
                  <p className="text-gray-600">Kızarıklık, kaşıntı veya sertlik oluşabilir; genellikle kısa sürede tedavi edilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Dolgu markası önemli mi?</h3>
                  <p className="text-gray-600">Evet, FDA onaylı ve saflaştırılmış markalarda risk minimumdur.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Dolgumu değiştirmem gerekir mi?</h3>
                  <p className="text-gray-600">Alerjik reaksiyon yaşanırsa evet; alternatif lidokainsiz form tercih edilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hyaluronidaz nedir?</h3>
                  <p className="text-gray-600">Hyaluronik asidi parçalayan enzimdir, alerji durumunda dolguyu güvenle eritir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Alerji testi yapılmalı mı?</h3>
                  <p className="text-gray-600">Geçmişte alerji öyküsü olan hastalarda önerilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Uzman Görüşü
              </h2>
              
              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-6">
                <p className="text-green-800 italic">
                  "Gerçek dolgu alerjisi çok nadir görülür, ancak bilinçli hekim seçimi her şeyi değiştirir.
                  Kullandığınız ürünün içeriğini ve kaynağını mutlaka öğrenin.
                  Güvenli dolgu, estetik kadar etik bir seçimdir."
                </p>
                <p className="text-green-800 font-medium mt-4">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣3️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak dolgusu alerjisi, doğru ürün ve steril çalışma koşullarında neredeyse imkânsızdır.
                Ancak hassas bünyelerde ön test yapılması, komplikasyon riskini sıfıra yaklaştırır.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Cildin verdiği her tepki bir mesajdır.
                  Onu dinlersen, risk almadan da güzelleşebilirsin."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Dermal Filler Hypersensitivity Reactions Report"</li>
                  <li>FDA (2023) – "Hyaluronic Acid Fillers: Safety and Allergy Data"</li>
                  <li>Teoxane Clinical Research (2024) – "Lidocaine Sensitivity in HA Fillers"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Dolgu Uygulamalarında Alerji Yönetimi Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#dudakdolgu</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#alerji</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-pink-600 to-rose-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Dudak Dolgusu Uygulaması ve Güvenlik Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan dudak dolgusu uygulaması ve alerji riski hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Dudak dolgusu uygulaması ve alerji riski hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-pink-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Teknikleri</h4>
              <p className="text-gray-600 text-sm">Doğal, simetrik ve estetik sonuçlar için dudak dolgusu uygulama teknikleri.</p>
            </a>
            <a
              href="/tr/blog/en-iyi-dudak-dolgusu-markasi-dogallik-kalicilik-ve-guven"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">En İyi Dudak Dolgusu Markası</h4>
              <p className="text-gray-600 text-sm">Doğallık, kalıcılık ve güven açısından en iyi dudak dolgusu markaları.</p>
            </a>
            <a
              href="/tr/blog/botoksun-alerji-riski-gercek-tehlike-mi-yanlis-algi-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoksun Alerji Riski</h4>
              <p className="text-gray-600 text-sm">Botoks alerjisi gerçek tehlike mi, yanlış algı mı? Bilimsel veriler ve klinik gözlemler.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
